import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Empty,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, StopOutlined, PauseOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';

// Importación actualizada: API movida a features/erp/subscriptions/api
import { subscriptionsApi } from '../api/subscriptions.ts';
import { selectUserPermissions } from '@store/slices/userSlice.tsx';
import { useAppSelector } from '@store/store';
import type {
  CancelSubscriptionPayload,
  CustomerSubscriptionBase,
  CustomerSubscriptionDetail,
  PauseSubscriptionPayload,
  SubscriptionPlan,
  SubscriptionStatus,
} from '@app-types/subscriptions.ts';
// Importación actualizada: componente movido a la misma carpeta
import UsageDashboard from './UsageDashboard.tsx';

const { Text, Title } = Typography;

const statusColors: Record<SubscriptionStatus, string> = {
  active: 'success',
  paused: 'warning',
  canceled: 'default',
  expired: 'error',
  pending_payment: 'processing',
  draft: 'default',
};

type CreateFormValues = {
  plan_id: number;
  start_date: Dayjs;
  use_current_day?: boolean;
  auto_renew?: boolean;
};

type CancelFormValues = Pick<CancelSubscriptionPayload, 'reason' | 'cancel_immediately'>;

interface SubscriptionCardProps {
  subscription: CustomerSubscriptionBase;
  canWrite: boolean;
  onRefresh: () => void;
}

const SubscriptionCard = ({ subscription, canWrite, onRefresh }: SubscriptionCardProps): JSX.Element => {
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>('details');
  const [showCancel, setShowCancel] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'renew' | 'reactivate' | null>(null);

  const detailQuery = useQuery<CustomerSubscriptionDetail>({
    queryKey: ['subscription-detail', subscription.id],
    queryFn: () => subscriptionsApi.getSubscriptionDetail(subscription.id),
    enabled: activeTab !== 'details' || Boolean(subscription.plan),
    staleTime: 30_000,
    initialData: subscription as CustomerSubscriptionDetail,
  });

  const renewMutation = useMutation({
    mutationFn: () => subscriptionsApi.renewSubscription(subscription.id, {}),
    onSuccess: () => {
      message.success('Suscripción renovada');
      queryClient.invalidateQueries({ queryKey: ['subscription-detail', subscription.id] });
      onRefresh();
      setConfirmAction(null);
    },
    onError: () => message.error('No pudimos renovar la suscripción'),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => subscriptionsApi.reactivateSubscription(subscription.id),
    onSuccess: () => {
      message.success('Suscripción reactivada');
      queryClient.invalidateQueries({ queryKey: ['subscription-detail', subscription.id] });
      onRefresh();
      setConfirmAction(null);
    },
    onError: () => message.error('No pudimos reactivar la suscripción'),
  });

  const cancelMutation = useMutation({
    mutationFn: (payload: CancelSubscriptionPayload) =>
      subscriptionsApi.cancelSubscription(subscription.id, payload),
    onSuccess: () => {
      message.success('Suscripción cancelada');
      queryClient.invalidateQueries({ queryKey: ['subscription-detail', subscription.id] });
      onRefresh();
      setShowCancel(false);
    },
    onError: () => message.error('No pudimos cancelar la suscripción'),
  });

  const pauseMutation = useMutation({
    mutationFn: (payload: PauseSubscriptionPayload) =>
      subscriptionsApi.pauseSubscription(subscription.id, payload),
    onSuccess: () => {
      message.success('Suscripción pausada');
      queryClient.invalidateQueries({ queryKey: ['subscription-detail', subscription.id] });
      onRefresh();
      setShowPause(false);
    },
    onError: () => message.error('No pudimos pausar la suscripción'),
  });

  const detail = detailQuery.data ?? (subscription as CustomerSubscriptionDetail);
  const currentCycleId = detail?.current_cycle?.id;

  const actions = useMemo(() => {
    const items: JSX.Element[] = [];
    if (!canWrite) return items;
    if (subscription.status === 'active' || subscription.status === 'pending_payment') {
      items.push(
        <Button
          key="renew"
          icon={<ReloadOutlined />}
          onClick={() => setConfirmAction('renew')}
          type="link"
        >
          Renovar
        </Button>,
      );
      items.push(
        <Button key="pause" icon={<PauseOutlined />} onClick={() => setShowPause(true)} type="link">
          Pausar
        </Button>,
      );
      items.push(
        <Button key="cancel" icon={<StopOutlined />} danger type="link" onClick={() => setShowCancel(true)}>
          Cancelar
        </Button>,
      );
    }
    if (subscription.status === 'paused') {
      items.push(
        <Button
          key="reactivate"
          icon={<CheckCircleOutlined />}
          type="link"
          onClick={() => setConfirmAction('reactivate')}
        >
          Reactivar
        </Button>,
      );
      items.push(
        <Button key="cancelPaused" icon={<StopOutlined />} danger type="link" onClick={() => setShowCancel(true)}>
          Cancelar
        </Button>,
      );
    }
    return items;
  }, [canWrite, subscription.status]);

  const header = (
    <Row justify="space-between" align="middle" gutter={[8, 8]}>
      <Col flex="auto">
        <Space orientation="vertical" size={0}>
          <Title level={4} style={{ margin: 0 }}>
            {subscription.plan?.name ?? 'Plan'}
          </Title>
          <Text type="secondary">Inicio: {subscription.start_date}</Text>
        </Space>
      </Col>
      <Col>
        <Tag color={statusColors[subscription.status]}>{subscription.status}</Tag>
      </Col>
    </Row>
  );

  const footer = (
    <Row justify="space-between" align="middle" gutter={[8, 8]}>
      <Col>
        <Space orientation={screens.md ? 'horizontal' : 'vertical'} size="small">
          <Text>Próximo cobro: {detail?.next_billing_date ?? '—'}</Text>
          <Badge
            status={detail?.auto_renew ? 'processing' : 'default'}
            text={detail?.auto_renew ? 'Renovación automática' : 'Renovación manual'}
          />
        </Space>
      </Col>
      <Col>{actions}</Col>
    </Row>
  );

  return (
    <Card size={screens.md ? 'default' : 'small'} title={header} extra={footer} bordered>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={[
          {
            key: 'details',
            label: 'Detalles',
            children: (
              <Space orientation="vertical" size="small">
                <Text>Cliente ID: {subscription.customer_id}</Text>
                <Text>Plan ID: {subscription.plan_id}</Text>
                <Text>Estado: {subscription.status}</Text>
                <Text>Último cobro: {detail?.last_billing_date ?? '—'}</Text>
              </Space>
            ),
          },
          {
            key: 'cycles',
            label: 'Ciclos',
            children: detailQuery.isFetching ? (
              <Spin />
            ) : (
              <Space orientation="vertical" style={{ width: '100%' }}>
                {detail?.cycles?.length ? (
                  detail.cycles.map((cycle) => (
                    <Card key={cycle.id} size="small">
                      <Space orientation={screens.md ? 'horizontal' : 'vertical'}>
                        <Text strong>#{cycle.cycle_number}</Text>
                        <Text>{cycle.start_date} → {cycle.end_date}</Text>
                        <Tag color="blue">{cycle.status}</Tag>
                        {cycle.sale_order_id ? <Text type="secondary">SO #{cycle.sale_order_id}</Text> : null}
                      </Space>
                    </Card>
                  ))
                ) : (
                  <Text type="secondary">Sin ciclos</Text>
                )}
              </Space>
            ),
          },
          {
            key: 'usage',
            label: 'Consumo',
            children: (
              <UsageDashboard
                subscriptionId={subscription.id}
                currentCycleId={currentCycleId}
              />
            ),
          },
        ]}
      />

      <Modal
        open={confirmAction === 'renew'}
        onCancel={() => setConfirmAction(null)}
        onOk={() => renewMutation.mutate()}
        confirmLoading={renewMutation.isPending}
        title="Confirmar renovación"
      >
        <Text>Se generará el próximo ciclo de facturación.</Text>
      </Modal>

      <Modal
        open={confirmAction === 'reactivate'}
        onCancel={() => setConfirmAction(null)}
        onOk={() => reactivateMutation.mutate()}
        confirmLoading={reactivateMutation.isPending}
        title="Reactivar suscripción"
      >
        <Text>La suscripción volverá a estado activo.</Text>
      </Modal>

      <Modal
        open={showCancel}
        title="Cancelar suscripción"
        onCancel={() => setShowCancel(false)}
        footer={null}
      >
        <Form<CancelFormValues>
          layout="vertical"
          initialValues={{ reason: '', cancel_immediately: false }}
          onFinish={(values) => cancelMutation.mutate(values)}
        >
          <Form.Item
            label="Motivo"
            name="reason"
            rules={[{ required: true, message: 'Ingresá un motivo' }]}
          >
            <Input.TextArea rows={3} maxLength={255} />
          </Form.Item>
          <Form.Item name="cancel_immediately" valuePropName="checked">
            <Switch checkedChildren="Cancelar ahora" unCheckedChildren="Al final del ciclo" />
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowCancel(false)}>Cerrar</Button>
            <Button type="primary" danger htmlType="submit" loading={cancelMutation.isPending}>
              Cancelar
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal open={showPause} title="Pausar suscripción" onCancel={() => setShowPause(false)} footer={null}>
        <Form<PauseSubscriptionPayload>
          layout="vertical"
          initialValues={{ reason: '' }}
          onFinish={(values) => pauseMutation.mutate(values)}
        >
          <Form.Item label="Motivo" name="reason">
            <Input.TextArea rows={3} maxLength={255} />
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowPause(false)}>Cerrar</Button>
            <Button type="primary" htmlType="submit" loading={pauseMutation.isPending}>
              Pausar
            </Button>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
};

interface SubscriptionsTabProps {
  customerId: number;
}

const SubscriptionsTab = ({ customerId }: SubscriptionsTabProps): JSX.Element => {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);
  const canWrite =
    permissionSet.has('subscriptions:write') ||
    permissionSet.has('subscriptions:plans:write') ||
    permissionSet.has('subscriptions:admin');

  const subscriptionsQuery = useQuery<CustomerSubscriptionBase[]>({
    queryKey: ['customer-subscriptions', customerId],
    queryFn: () => subscriptionsApi.getCustomerSubscriptions(customerId),
    enabled: customerId > 0,
  });

  const plansQuery = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans', 'available'],
    queryFn: () => subscriptionsApi.getAvailablePlans(100),
  });

  const handleCreate = async (values: CreateFormValues) => {
    if (!values.start_date) {
      message.error('Ingresá la fecha de inicio');
      return;
    }
    setCreating(true);
    try {
      const policyOverrides = values.use_current_day ? { use_current_day: true } : undefined;
      const result = await subscriptionsApi.createSubscription({
        customer_id: customerId,
        plan_id: values.plan_id,
        start_date: dayjs(values.start_date).format('YYYY-MM-DD'),
        auto_renew: values.auto_renew,
        policy_overrides: policyOverrides,
      });
      message.success(
        result.initial_payment.eligible
          ? 'Suscripción creada con pago pendiente'
          : 'Suscripción creada; el cobro será al final del período',
      );
      queryClient.invalidateQueries({ queryKey: ['customer-subscriptions', customerId] });
      setCreateOpen(false);
    } catch (error) {
      console.error(error);
      message.error('No pudimos crear la suscripción');
    } finally {
      setCreating(false);
    }
  };

  const subscriptions = subscriptionsQuery.data ?? [];
  const isEmpty = !subscriptions.length && !subscriptionsQuery.isLoading;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Suscripciones
          </Title>
          <Text type="secondary">Historial y acciones del cliente</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
            disabled={!canWrite}
          >
            Crear suscripción
          </Button>
        </Col>
      </Row>

      {subscriptionsQuery.isLoading ? (
        <Spin />
      ) : isEmpty ? (
        <Card>
          <Space orientation="vertical" align="center" style={{ width: '100%' }}>
            <Empty description="Sin suscripciones activas" imageStyle={{ height: 80 }} />
            {canWrite ? (
              <Button type="primary" onClick={() => setCreateOpen(true)}>
                Crear suscripción
              </Button>
            ) : null}
          </Space>
        </Card>
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {subscriptions.map((sub) => (
            <SubscriptionCard
              key={sub.id}
              subscription={sub}
              canWrite={canWrite}
              onRefresh={() =>
                queryClient.invalidateQueries({ queryKey: ['customer-subscriptions', customerId] })
              }
            />
          ))}
        </Space>
      )}

      <Modal
        open={createOpen}
        title="Crear suscripción"
        onCancel={() => setCreateOpen(false)}
        footer={null}
      >
        <Form<CreateFormValues>
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ auto_renew: true, start_date: dayjs(), use_current_day: false }}
        >
          <Form.Item
            label="Plan"
            name="plan_id"
            rules={[{ required: true, message: 'Seleccioná un plan' }]}
          >
            <Select
              placeholder="Elegí un plan"
              options={(plansQuery.data ?? []).map((plan) => ({
                label: plan.name,
                value: plan.id,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="Fecha de inicio"
            name="start_date"
            rules={[{ required: true, message: 'Ingresá la fecha de inicio' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Renovación automática" name="auto_renew" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>
          <Collapse
            ghost
            items={[{
              key: 'advanced',
              label: 'Configuración avanzada',
              children: (
                <Form.Item name="use_current_day" valuePropName="checked">
                  <Checkbox>Usar el día de hoy como vencimiento</Checkbox>
                </Form.Item>
              ),
            }]}
          />
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setCreateOpen(false)}>Cerrar</Button>
            <Button type="primary" htmlType="submit" loading={creating} disabled={creating}>
              Crear
            </Button>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
};

export default SubscriptionsTab;
